import { fetchSnykPages, SnykPagesDeps } from './fetch-snyk-pages';

/** Pinned the same way every Snyk REST call in this monorepo pins it. */
const SNYK_API_VERSION = '2024-01-23';
const PAGE_SIZE = 100;

export interface SnykOrg {
  id: string;
  /** URL segment - app.snyk.io/org/{slug}. Often carries a random suffix and
   * is NOT what Snyk's own UI displays. */
  slug: string;
  /** What Snyk's UI calls the org, e.g. "Port". Use this for anything a human reads. */
  name: string;
}

/**
 * Lists every org in a Snyk group, sorted alphabetically by name.
 *
 * The typed loader shared by snyk-branch-view's useSnykOrgs hook (which wraps
 * this in React state/effect) and scaffolder-fields' SnykOrgField (which
 * wraps it in useAsync) - both needed the same request, pagination and
 * attribute mapping, so it lives here once rather than twice.
 */
export async function fetchSnykOrgs(
  groupId: string,
  deps: SnykPagesDeps,
): Promise<SnykOrg[]> {
  const collected: SnykOrg[] = [];

  await fetchSnykPages<{
    id: string;
    attributes?: { slug?: string; name?: string };
  }>(
    `https://api.snyk.io/rest/orgs?version=${SNYK_API_VERSION}` +
      `&group_id=${encodeURIComponent(groupId)}&limit=${PAGE_SIZE}`,
    deps,
    {
      onNotOk: status =>
        new Error(
          `Snyk returned ${status} listing organizations. ` +
            `Check that SNYK_TOKEN can read group ${groupId}.`,
        ),
      staleAfterJson: false,
    },
    orgsPage => {
      for (const org of orgsPage) {
        const attrs = org.attributes ?? {};
        collected.push({
          id: org.id,
          slug: attrs.slug ?? org.id,
          name: attrs.name ?? attrs.slug ?? org.id,
        });
      }
    },
  );

  collected.sort((a, b) => a.name.localeCompare(b.name));
  return collected;
}
